import { useState, useRef, useCallback, useEffect, useLayoutEffect } from "react";

import { genId, ICON, LEAF_TYPES, T } from "./Utils/exports";
import { ANIMATION_KEYFRAMES } from "./Utils/AnimationHelper";
import { mkGroup, mkLeaf, mkScreen } from "./Utils/Makers";
import { getAlignmentGuides } from "./Utils/AlignmentGuidesHelper";
import { HIDE_SCROLLBAR_CSS } from "./Utils/CssHelper";
import { collectInputVars, deepReId, deleteNodeFromTree, findNode, insertNode, moveNode, updateNode } from "./Utils/TreeHelper";
import { Canvas } from "./Utils/Canvas";
import { LayerItem } from "./Utils/LayerHelper";
import { StylePanel } from "./Utils/StylesPanel";
import { ContentPanel } from "./Utils/ContentPanel";
import { DataGroupPanel } from "./Utils/DataGroupPanel";
import { DataSourcesPanel } from "./Utils/DataSourcePanel";
import { CatalogPanel } from "./Utils/CatalogPanel";
import { CatalogContext } from "./Widgets/catalog";
import { KioskSettingsPanel } from "./Utils/KioskSettingsPanel";
import { DEFAULT_KIOSK_SETTINGS } from "./Widgets/KioskIdle";

export default function KioskEditor() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [admin] = useState(() => {
    if (new URLSearchParams(window.location.search).get("admin") === "true") return true;
    try { return JSON.parse(sessionStorage.getItem("kioskStudioRef") || "{}").admin === true; } catch { return false; }
  });
  const [screens, setScreens] = useState([mkScreen(1)]);
  const [dataSources, setDataSources] = useState([]);
  const [catalog, setCatalog] = useState({ currency: "₹", products: [] });
  const [settings, setSettings] = useState({ ...DEFAULT_KIOSK_SETTINGS });
  const [sourcePreviewData, setSourcePreviewData] = useState({});
  const [activeScreenId, setActiveScreenId] = useState(null);
  const [selPath, setSelPath] = useState(null);
  const [rightTab, setRightTab] = useState("style");
  const [manualZoom, setManualZoom] = useState(null);
  const [autoZoom, setAutoZoom] = useState(0.45);
  const [leftCollapsed, setLC] = useState(false);
  const [rightCollapsed, setRC] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [dragInfo, setDragInfo] = useState(null);
  const [dragGuides, setDragGuides] = useState(null);
  const [clipboard, setClipboard] = useState(null); 
  const [pasteToast, setPasteToast] = useState(null); 
  const [previewData, setPreviewDataRaw] = useState({});
  const [runtimeVars, setRuntimeVars] = useState({});
  const canvasWrap = useRef(null);
  const screenItemRefs = useRef({});
  const prevTopsRef = useRef({});

  const setPreviewData = useCallback((id, data) => setPreviewDataRaw(p => { const n = { ...p }; if (data == null) delete n[id]; else n[id] = data; return n; }), []);
  const onVarChange = useCallback((name, val) => setRuntimeVars(p => ({ ...p, [name]: val })), []);

  const screen = screens.find(s => s.id === activeScreenId) || screens[0];
  const scW = screen?.styles.width || 390; const scH = screen?.styles.height || 844;
  const zoom = manualZoom ?? autoZoom;
  const selNode = selPath ? findNode(screen.children, selPath) : null;
  const isGroupSelected = selNode?.type === "group";
  const allInputVars = collectInputVars(screen.children);

  const addTargetPath = isGroupSelected ? selPath : (selPath && selPath.length > 1 && findNode(screen.children, selPath.slice(0, -1))?.type === "group") ? selPath.slice(0, -1) : null;
  const addTargetNode = addTargetPath ? findNode(screen.children, addTargetPath) : null;

  useLayoutEffect(() => {
    if (!canvasWrap.current) return;
    const fit = () => { if (!canvasWrap.current) return; const { clientWidth: cw, clientHeight: ch } = canvasWrap.current; setAutoZoom(Math.min((cw - 48) / scW, (ch - 48) / scH, 1)); };
    fit(); const obs = new ResizeObserver(fit); obs.observe(canvasWrap.current); return () => obs.disconnect();
  }, [scW, scH, leftCollapsed, rightCollapsed]);

  // FLIP animation for screen list reordering
  useLayoutEffect(() => {
    const snap = prevTopsRef.current;
    if (!Object.keys(snap).length) return;
    Object.entries(screenItemRefs.current).forEach(([sid, el]) => {
      if (!el || snap[sid] == null) return;
      const dy = snap[sid] - el.getBoundingClientRect().top;
      if (Math.abs(dy) < 1) return;
      el.style.transition = "none";
      el.style.transform = `translateY(${dy}px)`;
      el.getBoundingClientRect(); // force reflow so browser registers the starting position
      el.style.transition = "transform 0.22s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
      el.style.transform = "";
    });
    prevTopsRef.current = {};
  });

  const updScreen = useCallback((id, fn) => setScreens(p => p.map(s => s.id === id ? fn(s) : s)), []);
  const updCur = useCallback(fn => updScreen(screen.id, fn), [screen, updScreen]);

  const deleteNode_ = useCallback((path) => { updCur(s => ({ ...s, children: deleteNodeFromTree(s.children, path) })); setSelPath(null); }, [updCur]);

  const moveSelectedNode = useCallback((dx, dy) => {
    if (!selPath || !selPath.length) return;
    const node = findNode(screen.children, selPath); if (!node) return;
    updCur(s => ({ ...s, children: updateNode(s.children, selPath, n => ({ ...n, styles: { ...n.styles, x: n.styles.x + dx, y: n.styles.y + dy } })) }));
  }, [selPath, screen.children, updCur]);

  const pasteToastTimer = useRef(null);
  const showPasteToast = useCallback((msg, isError = false) => {
    if (pasteToastTimer.current) clearTimeout(pasteToastTimer.current);
    setPasteToast({ msg, isError });
    pasteToastTimer.current = setTimeout(() => setPasteToast(null), 2200);
  }, []);

  const studioRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  const applyInitialDimensions = useCallback((ref) => {
    if (!ref.resolution) return;
    let [w, h] = ref.resolution.split("x").map(Number);
    if (!w || !h) return;
    if (ref.orientation === "landscape" && w < h) [w, h] = [h, w];
    if (ref.orientation === "portrait" && w > h) [w, h] = [h, w];
    setScreens(prev => [{ ...prev[0], styles: { ...prev[0].styles, width: w, height: h } }]);
  }, []);

  useEffect(() => {
    const load = async () => {
      // Missing ref means this tab was genuinely not opened via ELM Studio — the only real "unauthorized" case.
      const refRaw = sessionStorage.getItem("kioskStudioRef");
      if (!refRaw) {
        if (!admin) setUnauthorized(true);
        setLoading(false);
        return;
      }

      let ref;
      try {
        ref = JSON.parse(refRaw);
      } catch (_) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }
      studioRef.current = ref;

      // A valid ref was found — from here on, any failure (network/API) is NOT an "unauthorized" case,
      // it just means the existing template couldn't be loaded, so fall back to a fresh canvas.
      try {
        if (ref.templateId) {
          const apiBase = localStorage.getItem("api");
          const token = localStorage.getItem("token") || sessionStorage.getItem("token");
          const reqBody = {
            path: "kioskTemplate/single",
            query: { id: ref.templateId },
            emmsId: ref.emmsId,
            body: {},
            requestType: "GET",
            headers: { "Content-Type": "application/json", "x-api-id": ref.apiId, "x-api-key": ref.apiKey },
          };
          const res = await fetch(apiBase + `/emms/piggyback`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(reqBody),
          });
          const item = await res.json();
          const rd = item?.customProperties?.kioskReferenceData;
          if (rd) {
            const parsed = typeof rd === "string" ? JSON.parse(rd) : rd;
            if (parsed.screens?.length) {
              setScreens(parsed.screens);
              setDataSources(Array.isArray(parsed.dataSources) ? parsed.dataSources : []);
              if (parsed.catalog) setCatalog(parsed.catalog);
              setSettings({ ...DEFAULT_KIOSK_SETTINGS, ...(parsed.settings || {}) });
              setActiveScreenId(parsed.screens[0].id);
              setLoading(false);
              return;
            }
          }
        }
      } catch (e) {
        console.error("Failed to load existing kiosk template, starting from a fresh canvas:", e);
      }
      applyInitialDimensions(ref);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    const onKeyDown = e => {
      const target = e.target;
      const inInput = target && target.closest && target.closest("input,textarea,select,[contenteditable='true']");

      if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C") && !inInput) {
        if (!selPath || !selPath.length) return;
        const node = findNode(screen.children, selPath);
        if (!node) return;
        e.preventDefault();
        setClipboard({ node: JSON.parse(JSON.stringify(node)), sourceScreenId: screen.id });
        showPasteToast(`📋 Copied "${node.name || node.type}"`);
        return;
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === "v" || e.key === "V") && !inInput) {
        if (!clipboard) return;
        e.preventDefault();
        const pasted = deepReId(clipboard.node);
        const isSameScreen = clipboard.sourceScreenId === screen.id;
        pasted.styles = {
          ...pasted.styles,
          x: (pasted.styles.x || 0) + (isSameScreen ? 20 : 0),
          y: (pasted.styles.y || 0) + (isSameScreen ? 20 : 0),
        };
        if (pasted.type === "group") pasted.name = (pasted.name || "Group") + " Copy";
        const targetPath = isGroupSelected ? selPath : null;
        updCur(s => ({ ...s, children: insertNode(s.children, targetPath, pasted) }));
        setSelPath(targetPath ? [...targetPath, pasted.id] : [pasted.id]);
        const crossScreen = !isSameScreen;
        showPasteToast(`✓ Pasted "${pasted.name || pasted.type}"${crossScreen ? " (from another screen)" : ""}`);
        return;
      }

      if (!selPath || !selPath.length) return;
      if (inInput) return;
      const step = e.shiftKey ? 10 : 1;
      if ((e.key === "d" || e.key === "D") && (e.shiftKey || e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const node = findNode(screen.children, selPath);
        if (!node) return;
        const clone = JSON.parse(JSON.stringify(node));
        clone.id = genId(clone.type || "node");
        if (clone.type === "group") clone.name = (clone.name || "Group") + " Copy";
        clone.styles = { ...clone.styles, x: (clone.styles.x || 0) + 16, y: (clone.styles.y || 0) + 16 };
        const parentPath = selPath.slice(0, -1);
        updCur(s => ({ ...s, children: insertNode(s.children, parentPath.length ? parentPath : null, clone) }));
        setSelPath(parentPath.length ? [...parentPath, clone.id] : [clone.id]);
        return;
      }
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault(); deleteNode_(selPath);
        return;
      }
      const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (moves[e.key]) {
        e.preventDefault(); const [dx, dy] = moves[e.key]; moveSelectedNode(dx, dy);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selPath, deleteNode_, moveSelectedNode, screen, updCur, clipboard, isGroupSelected, showPasteToast]);

  const addScreen = () => { const s = mkScreen(screens.length + 1); setScreens(p => [...p, s]); setActiveScreenId(s.id); setSelPath(null); };
  const delScreen = id => { setScreens(p => { const n = p.filter(s => s.id !== id); return n.length ? n : [mkScreen(1)]; }); setSelPath(null); };
  const dupScreen = id => { const src = screens.find(s => s.id === id); if (!src) return; const d = JSON.parse(JSON.stringify(src)); d.id = genId("screen"); d.name = d.name + " Copy"; setScreens(p => [...p, d]); setActiveScreenId(d.id); };
  const moveScreen = (id, dir) => {
    // snapshot positions for FLIP animation before state update
    const snap = {};
    Object.entries(screenItemRefs.current).forEach(([sid, el]) => { if (el) snap[sid] = el.getBoundingClientRect().top; });
    prevTopsRef.current = snap;
    setScreens(p => { const i = p.findIndex(s => s.id === id); const j = dir === "up" ? i - 1 : i + 1; if (j < 0 || j >= p.length) return p; const a = [...p]; [a[i], a[j]] = [a[j], a[i]]; return a; });
  };

  const addNodeToTarget = n => { updCur(s => ({ ...s, children: insertNode(s.children, addTargetPath, n) })); setSelPath(addTargetPath ? [...addTargetPath, n.id] : [n.id]); };
  const addLeaf = type => addNodeToTarget(mkLeaf(type));
  const addGroup = () => addNodeToTarget(mkGroup());
  const updateNode_ = (path, n) => updCur(s => ({ ...s, children: updateNode(s.children, path, () => n) }));
  const renameNode = (path, name) => updCur(s => ({ ...s, children: updateNode(s.children, path, n => ({ ...n, name })) }));

  const moveNodeOut = path => {
    if (path.length <= 1) return;
    const pp = path.slice(0, -1); const gp = pp.slice(0, -1);
    updCur(s => ({ ...s, children: moveNode(s.children, path, gp) }));
    const node = findNode(screen.children, path); if (node) setSelPath([...gp, node.id]);
  };

  const handleLayerDrop = (fromPath, toParentPath, toSiblingId, position) => {
    if (!fromPath) return;
    if (toSiblingId == null) {
      updCur(s => ({ ...s, children: moveNode(s.children, fromPath, toParentPath) }));
      const node = findNode(screen.children, fromPath); if (node) setSelPath([...toParentPath, node.id]);
    } else {
      const siblings = toParentPath.length === 0 ? screen.children : (findNode(screen.children, toParentPath)?.children || []);
      const sibIdx = siblings.findIndex(c => c.id === toSiblingId);
      const insertIdx = position === "after" ? sibIdx + 1 : sibIdx;
      const sameParent = JSON.stringify(fromPath.slice(0, -1)) === JSON.stringify(toParentPath);
      const fromIdx = siblings.findIndex(c => c.id === fromPath[fromPath.length - 1]);
      const adj = sameParent && fromIdx < insertIdx ? insertIdx - 1 : insertIdx;
      updCur(s => ({ ...s, children: moveNode(s.children, fromPath, toParentPath, Math.max(0, adj)) }));
      const node = findNode(screen.children, fromPath); if (node) setSelPath([...toParentPath, node.id]);
    }
  };

  const startDrag = useCallback((e, path, ox, oy) => {
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY;
    const node = findNode(screen.children, path); if (!node) return;
    const nw = node.styles.width, nh = node.styles.height; const topLevel = path.length === 1;
    const mv = me => {
      const dx = (me.clientX - sx) / zoom, dy = (me.clientY - sy) / zoom; let nx = Math.round(ox + dx), ny = Math.round(oy + dy); if (topLevel) { nx = Math.max(0, Math.min(scW - nw, nx)); ny = Math.max(0, Math.min(scH - nh, ny)); }
      const tempNode = { ...node, styles: { ...node.styles, x: nx, y: ny } };
      const guides = getAlignmentGuides(tempNode, screen.children, scW, scH);
      const snapThreshold = 5;
      guides.vLines.forEach(gx => { if (Math.abs(nx - gx) <= snapThreshold) nx = gx; if (Math.abs((nx + nw) - gx) <= snapThreshold) nx = gx - nw; if (Math.abs((nx + nw / 2) - gx) <= snapThreshold) nx = gx - nw / 2; });
      guides.hLines.forEach(gy => { if (Math.abs(ny - gy) <= snapThreshold) ny = gy; if (Math.abs((ny + nh) - gy) <= snapThreshold) ny = gy - nh; if (Math.abs((ny + nh / 2) - gy) <= snapThreshold) ny = gy - nh / 2; });
      const snappedNode = { ...node, styles: { ...node.styles, x: nx, y: ny } };
      setDragGuides(getAlignmentGuides(snappedNode, screen.children, scW, scH));
      updCur(s => ({ ...s, children: updateNode(s.children, path, n => ({ ...n, styles: { ...n.styles, x: nx, y: ny } })) }));
    };
    const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); setDragGuides(null); };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
  }, [screen, zoom, updCur, scW, scH]);

  const startResize = useCallback((e, path, handleId) => {
    e.preventDefault(); e.stopPropagation();
    const node = findNode(screen.children, path); if (!node) return;
    const { x, y, width, height } = node.styles; const sx = e.clientX, sy = e.clientY;
    const mv = me => {
      const dx = (me.clientX - sx) / zoom, dy = (me.clientY - sy) / zoom; let nx = x, ny = y, nw = width, nh = height; if (handleId.includes("e")) nw = Math.max(20, width + dx); if (handleId.includes("s")) nh = Math.max(20, height + dy); if (handleId.includes("w")) { nx = x + dx; nw = Math.max(20, width - dx); } if (handleId.includes("n")) { ny = y + dy; nh = Math.max(20, height - dy); }
      const tempNode = { ...node, styles: { ...node.styles, x: Math.round(nx), y: Math.round(ny), width: Math.round(nw), height: Math.round(nh) } };
      const guides = getAlignmentGuides(tempNode, screen.children, scW, scH);
      const snapThreshold = 5;
      if (handleId.includes("w") || handleId.includes("e")) { 
        guides.vLines.forEach(gx => { if (Math.abs(nx - gx) <= snapThreshold) nx = gx; if (Math.abs((nx + nw) - gx) <= snapThreshold) { if (handleId.includes("w")) nx = gx - nw; else nw = gx - nx; } if (Math.abs((nx + nw / 2) - gx) <= snapThreshold) { if (handleId.includes("w")) nx = gx - nw / 2; else nw = (gx - nx) * 2; } });
      }
      if (handleId.includes("n") || handleId.includes("s")) { 
        guides.hLines.forEach(gy => { if (Math.abs(ny - gy) <= snapThreshold) ny = gy; if (Math.abs((ny + nh) - gy) <= snapThreshold) { if (handleId.includes("n")) ny = gy - nh; else nh = gy - ny; } if (Math.abs((ny + nh / 2) - gy) <= snapThreshold) { if (handleId.includes("n")) ny = gy - nh / 2; else nh = (gy - ny) * 2; } });
      }
      const snappedNode = { ...node, styles: { ...node.styles, x: Math.round(nx), y: Math.round(ny), width: Math.round(nw), height: Math.round(nh) } };
      setDragGuides(getAlignmentGuides(snappedNode, screen.children, scW, scH));
      updCur(s => ({ ...s, children: updateNode(s.children, path, n => ({ ...n, styles: { ...n.styles, x: Math.round(nx), y: Math.round(ny), width: Math.round(nw), height: Math.round(nh) } })) }));
    };
    const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); setDragGuides(null); };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
  }, [screen, zoom, updCur, scW, scH]);

  const LP = leftCollapsed ? 28 : 220; const RP = rightCollapsed ? 28 : 264;
  const PanelToggle = ({ collapsed, onClick, dir }) => (
    <button onClick={onClick} style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", [dir === "left" ? "right" : "left"]: collapsed ? 4 : -8, width: 14, height: 32, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 3, color: T.textSub, cursor: "pointer", fontSize: 8, padding: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {collapsed ? (dir === "left" ? "›" : "‹") : (dir === "left" ? "‹" : "›")}
    </button>
  );

  const breadcrumb = selPath ? selPath.map((id, i) => { const n = findNode(screen.children, selPath.slice(0, i + 1)); return (i > 0 ? " › " : "") + (n?.name || n?.type || "?"); }).join("") : null;
  const rightTabs = isGroupSelected
    ? [["style", "Style"], ["groupdata", "Data"], ["datasources", "Sources"], ["catalog", "Catalog"], ["kiosk", "Kiosk"]]
    : [["style", "Style"], ["content", "Data"], ["datasources", "Sources"], ["catalog", "Catalog"], ["kiosk", "Kiosk"]];

  const exportJSON = () => JSON.stringify({ screens, dataSources, catalog, settings }, null, 2);

  const playgroundFileRef = useRef(null);
  const handleSaveJson = () => {
    const blob = new Blob([exportJSON()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "kiosk.json"; a.click();
    URL.revokeObjectURL(url);
  };
  const handleLoadJson = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (parsed.screens) setScreens(parsed.screens);
        if (parsed.dataSources) setDataSources(parsed.dataSources);
        if (parsed.catalog) setCatalog(parsed.catalog);
        if (parsed.settings) setSettings({ ...DEFAULT_KIOSK_SETTINGS, ...parsed.settings });
        showPasteToast("✓ Loaded");
      } catch { showPasteToast("✕ Invalid JSON", true); }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleSave = async () => {
    const ref = studioRef.current;

    const apiBase = localStorage.getItem("api");
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const reqBody = {
      path: "kioskTemplate",
      query: {},
      emmsId: ref.emmsId,
      body: { id: ref.templateId, customProperties: { kioskReferenceData: { screens, dataSources, catalog, settings } } },
      requestType: "PUT",
      headers: { "Content-Type": "application/json", "x-api-id": ref.apiId, "x-api-key": ref.apiKey },
    };
    setSaving(true);
    try {
      const res = await fetch(apiBase + `/emms/piggyback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(reqBody),
      });
      const saved = await res.json();
      localStorage.setItem("kioskPreviewRef", JSON.stringify({ ...ref, savedResponse: saved, savedAt: Date.now() }));
      showPasteToast("✓ Saved");
      return saved;
    } catch (e) {
      showPasteToast("✕ Save failed", true);
    } finally {
      setSaving(false);
    }
  };

  if (isMobile) return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.bg, color: T.textSub, fontFamily: "system-ui,sans-serif", gap: 14, textAlign: "center", padding: 24 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 900, color: "#fff" }}>K</div>
      <div style={{ fontSize: 13, maxWidth: 280 }}>The Kiosk Editor is only supported on desktop. Please open this page on a larger screen.</div>
      <button onClick={() => window.close()} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 4, color: T.textSub, cursor: "pointer", fontSize: 11, padding: "6px 16px", fontFamily: "inherit" }}>Close</button>
    </div>
  );

  if (unauthorized) return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.bg, color: T.textSub, fontFamily: "system-ui,sans-serif", gap: 14 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 900, color: "#fff" }}>K</div>
      <div style={{ fontSize: 13 }}>This editor must be opened from ELM Studio.</div>
      <button onClick={() => window.close()} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 4, color: T.textSub, cursor: "pointer", fontSize: 11, padding: "6px 16px", fontFamily: "inherit" }}>Close</button>
    </div>
  );

  if (loading) return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: T.bg, color: T.textSub, fontFamily: "system-ui,sans-serif", gap: 14 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 900, color: "#fff" }}>K</div>
      <div style={{ width: 24, height: 24, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.accent}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: 12 }}>Loading template…</div>
    </div>
  );

  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", background: T.bg, color: T.text, fontFamily: "system-ui,sans-serif", overflow: "hidden", fontSize: 11 }}>
      <style>{HIDE_SCROLLBAR_CSS + "\n" + ANIMATION_KEYFRAMES}</style>

      {/* ── LEFT PANEL ── */}
      <div style={{ width: LP, minWidth: LP, background: T.bgPanel, borderRight: `1px solid ${T.border}`, display: "flex", flexDirection: "column", flexShrink: 0, position: "relative", transition: "width 0.15s,min-width 0.15s", overflow: "hidden" }}>
        <PanelToggle collapsed={leftCollapsed} onClick={() => setLC(!leftCollapsed)} dir="left" />
        {!leftCollapsed && <>
          <div style={{ padding: "6px 7px 4px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: T.accentLt, textTransform: "uppercase", letterSpacing: "0.1em" }}>Screens</span>
              <button onClick={addScreen} style={{ background: T.accent, border: "none", borderRadius: 3, color: "#fff", cursor: "pointer", fontSize: 13, width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, padding: 0 }}>+</button>
            </div>
            <div style={{ maxHeight: "22vh", overflowY: "auto" }}>
              {screens.map((sc, idx) => (
                <div key={sc.id}
                  ref={el => { if (el) screenItemRefs.current[sc.id] = el; else delete screenItemRefs.current[sc.id]; }}
                  onClick={() => { setActiveScreenId(sc.id); setSelPath(null); }}
                  style={{ display: "flex", alignItems: "center", gap: 3, padding: "3px 4px", borderRadius: 3, marginBottom: 2, cursor: "pointer", background: screen.id === sc.id ? T.bgCard : "transparent", border: screen.id === sc.id ? `1px solid ${T.border}` : "1px solid transparent", willChange: "transform" }}>
                  <div style={{ width: 12, height: 18, background: sc.styles.backgroundColor, borderRadius: 2, border: `1px solid ${T.border}`, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 9, color: screen.id === sc.id ? T.text : T.textSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sc.name}</span>
                  {sc.id === ((settings.homeScreenId && screens.some(x => x.id === settings.homeScreenId)) ? settings.homeScreenId : screens[0]?.id) && <span title="Home screen" style={{ fontSize: 9, color: T.green, flexShrink: 0 }}>⌂</span>}
                  <div style={{ display: "flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
                    <button onClick={e => { e.stopPropagation(); moveScreen(sc.id, "up"); }} disabled={idx === 0} style={{ background: "none", border: "none", color: idx === 0 ? T.textMute + "44" : T.textMute, cursor: idx === 0 ? "default" : "pointer", fontSize: 7, padding: 0, lineHeight: 1, display: "block" }}>▲</button>
                    <button onClick={e => { e.stopPropagation(); moveScreen(sc.id, "down"); }} disabled={idx === screens.length - 1} style={{ background: "none", border: "none", color: idx === screens.length - 1 ? T.textMute + "44" : T.textMute, cursor: idx === screens.length - 1 ? "default" : "pointer", fontSize: 7, padding: 0, lineHeight: 1, display: "block" }}>▼</button>
                  </div>
                  <button onClick={e => { e.stopPropagation(); dupScreen(sc.id); }} style={{ background: "none", border: "none", color: T.textMute, cursor: "pointer", fontSize: 9, padding: 0 }}>⧉</button>
                  <button onClick={e => { e.stopPropagation(); delScreen(sc.id); }} style={{ background: "none", border: "none", color: T.textMute, cursor: "pointer", fontSize: 9, padding: 0 }}>✕</button>
                </div>
              ))}
            </div>
          </div>
          <div style={{ padding: "5px 7px 5px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: T.accentLt, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>{addTargetNode ? `Into: ${addTargetNode.name}` : "Add to Screen"}</span>
              {addTargetPath && <button onClick={() => setSelPath(null)} style={{ background: "none", border: "none", color: T.textSub, fontSize: 8, cursor: "pointer", padding: 0 }}>✕</button>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, marginBottom: 3 }}>
              {LEAF_TYPES.map(t => (
                <button key={t} onClick={() => addLeaf(t)} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 3, color: T.textSub, cursor: "pointer", fontSize: 9, padding: "4px 3px", display: "flex", alignItems: "center", gap: 3 }} onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentHi; e.currentTarget.style.color = T.text; }} onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSub; }}>
                  <span style={{ fontSize: 10 }}>{ICON[t] || "□"}</span>
                  <span style={{ textTransform: "capitalize", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t}</span>
                </button>
              ))}
            </div>
            <button onClick={addGroup} style={{ width: "100%", background: T.bg, border: `1px solid ${T.yellow}44`, borderRadius: 3, color: T.yellow, cursor: "pointer", fontSize: 9, padding: "4px 3px", display: "flex", alignItems: "center", gap: 3, justifyContent: "center" }} onMouseEnter={e => e.currentTarget.style.borderColor = T.yellow} onMouseLeave={e => e.currentTarget.style.borderColor = `${T.yellow}44`}>
              <span>⬡</span><span>{addTargetNode ? "Nested Group" : "New Group"}</span>
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "5px 7px" }} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); if (dragInfo) { handleLayerDrop(dragInfo.path, [], null, "after"); setDragInfo(null); } }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: T.accentLt, textTransform: "uppercase", letterSpacing: "0.1em" }}>Layers</span>
              <span style={{ fontSize: 7, color: T.textMute }}>drag to reorder</span>
            </div>
            {[...screen.children].reverse().map(node => (
              <LayerItem key={node.id} node={node} path={[node.id]} depth={0} selPath={selPath} onSelect={setSelPath} onDelete={deleteNode_} renamingId={renamingId} setRenamingId={setRenamingId} onRename={renameNode} onDrop={handleLayerDrop} onMoveOut={moveNodeOut} dragInfo={dragInfo} setDragInfo={setDragInfo} />
            ))}
            {screen.children.length === 0 && <div style={{ color: T.textMute, fontSize: 9, textAlign: "center", paddingTop: 8 }}>No layers yet</div>}
          </div>
          {allInputVars.length > 0 && (
            <div style={{ borderTop: `1px solid ${T.border}`, padding: "4px 7px", flexShrink: 0, background: T.bg }}>
              <div style={{ fontSize: 7, color: T.textMute, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Live Variables</div>
              {allInputVars.map(v => (
                <div key={v.varName} style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 8, marginBottom: 2 }}>
                  <span style={{ color: T.blue, fontFamily: "monospace", minWidth: 50, flexShrink: 0 }}>${v.varName}</span>
                  <span style={{ color: T.textMute, flexShrink: 0 }}>=</span>
                  <span style={{ color: runtimeVars[v.varName] ? T.textSub : T.textMute, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "monospace" }}>{runtimeVars[v.varName] ? `"${runtimeVars[v.varName]}"` : '"" '}</span>
                </div>
              ))}
            </div>
          )}
        </>}
      </div>

      {/* ── CANVAS AREA ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        <div style={{ height: 36, background: T.bgPanel, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", padding: "0 8px", gap: 6, flexShrink: 0, overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <div style={{ width: 20, height: 20, borderRadius: 4, background: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#fff" }}>K</div>
            <span style={{ fontSize: 11, fontWeight: 700, color: T.text, whiteSpace: "nowrap" }}>KioskStudio</span>
          </div>
          <div style={{ width: 1, height: 16, background: T.border, flexShrink: 0 }} />
          {screen && <>
            <span style={{ fontSize: 9, color: T.accentLt, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>{screen.name}</span>
            {[["W", screen.styles.width, "width"], ["H", screen.styles.height, "height"]].map(([l, v, k]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                <span style={{ fontSize: 8, color: T.textMute }}>{l}</span>
                <input type="number" value={v} onChange={e => updCur(s => ({ ...s, styles: { ...s.styles, [k]: Number(e.target.value) } }))} style={{ width: 42, background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 3, color: T.text, padding: "1px 3px", fontSize: 10 }} />
              </div>
            ))}
            <input type="color" value={screen.styles.backgroundColor} onChange={e => updCur(s => ({ ...s, styles: { ...s.styles, backgroundColor: e.target.value } }))} style={{ width: 18, height: 16, border: `1px solid ${T.border}`, borderRadius: 2, background: "none", cursor: "pointer", padding: 0, flexShrink: 0 }} />
            <input value={screen.styles.backgroundImage || ""} onChange={e => updCur(s => ({ ...s, styles: { ...s.styles, backgroundImage: e.target.value } }))} placeholder="BG image URL…" style={{ flex: 1, minWidth: 0, maxWidth: 130, background: T.bgInput, border: `1px solid ${T.border}`, borderRadius: 3, color: T.text, padding: "2px 4px", fontSize: 9 }} />
          </>}
          <div style={{ flex: 1 }} />
          {breadcrumb && <div style={{ fontSize: 8, color: T.accentLt, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 180, flexShrink: 0 }}>{breadcrumb}</div>}
          <div style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            {[[null, "Auto"], [0.25, "25%"], [0.5, "50%"], [0.75, "75%"], [1, "100%"]].map(([z, l]) => (
              <button key={l} onClick={() => setManualZoom(z)} style={{ background: manualZoom === z ? T.accent : T.bg, border: `1px solid ${T.border}`, borderRadius: 3, color: manualZoom === z ? "#fff" : T.textSub, cursor: "pointer", fontSize: 8, padding: "2px 4px", whiteSpace: "nowrap" }}>{l}</button>
            ))}
          </div>
          {admin ? (
            <>
              <input ref={playgroundFileRef} type="file" accept=".json" style={{ display: "none" }} onChange={handleLoadJson} />
              <button onClick={() => playgroundFileRef.current?.click()} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 3, color: T.textSub, cursor: "pointer", fontSize: 9, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>⬆ Load JSON</button>
              <button onClick={async () => {
                if (studioRef.current) {
                  const saved = await handleSave();
                  if (saved) window.open("/kiosk-preview", "_blank");
                } else {
                  sessionStorage.setItem("kioskPreviewRef", JSON.stringify({ directData: { screens, dataSources, catalog, settings } }));
                  window.open("/kiosk-preview", "_blank");
                }
              }} style={{ background: T.green, border: "none", borderRadius: 3, color: "#fff", cursor: "pointer", fontSize: 9, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0, fontWeight: 600 }}>▶ Preview</button>
              <button onClick={handleSaveJson} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 3, color: T.textSub, cursor: "pointer", fontSize: 9, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>⬇ Save JSON</button>
            </>
          ) : (
            <>
              <input ref={playgroundFileRef} type="file" accept=".json" style={{ display: "none" }} onChange={handleLoadJson} />
              <button onClick={() => playgroundFileRef.current?.click()} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 3, color: T.textSub, cursor: "pointer", fontSize: 9, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>⬆ Load JSON</button>
              <button onClick={handleSaveJson} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 3, color: T.textSub, cursor: "pointer", fontSize: 9, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0 }}>⬇ Save JSON</button>
              <button onClick={async () => {
                const saved = await handleSave();
                if (saved) window.open("/kiosk-preview", "_blank");
              }} style={{ background: T.green, border: "none", borderRadius: 3, color: "#fff", cursor: "pointer", fontSize: 9, padding: "3px 8px", whiteSpace: "nowrap", flexShrink: 0, fontWeight: 600 }}>▶ Preview</button>
              <button onClick={handleSave} disabled={saving || !studioRef.current} style={{ background: "#16a34a", border: "none", borderRadius: 4, color: "#fff", cursor: (saving || !studioRef.current) ? "not-allowed" : "pointer", fontSize: 11, padding: "4px 16px", whiteSpace: "nowrap", flexShrink: 0, fontWeight: 700, opacity: (saving || !studioRef.current) ? 0.5 : 1 }} title={!studioRef.current ? "No studio session — open from ELM Studio to save" : ""}>
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
        <div ref={canvasWrap} onClick={() => setSelPath(null)} style={{ flex: 1, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg, position: "relative" }}>
          <div style={{ position: "relative", width: scW * zoom, height: scH * zoom, flexShrink: 0 }}>
            <div style={{ position: "absolute", top: -16, left: 0, fontSize: 8, color: T.textMute, whiteSpace: "nowrap" }}>
              {screen.name} — {scW}×{scH} · {Math.round(zoom * 100)}%
              {selNode && <span style={{ color: isGroupSelected ? T.yellow : T.blueLt, marginLeft: 6 }}>{isGroupSelected ? `⬡ ${selNode.name}` : selNode.type}</span>}
            </div>
            <CatalogContext.Provider value={catalog}>
              <Canvas screen={screen} selPath={selPath} onSelect={setSelPath} onDragStart={startDrag} onResizeStart={startResize} zoom={zoom} previewData={previewData} runtimeVars={runtimeVars} onVarChange={onVarChange} dragGuides={dragGuides} globalSourceStore={sourcePreviewData} />
            </CatalogContext.Provider>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div style={{ width: RP, minWidth: RP, background: T.bgPanel, borderLeft: `1px solid ${T.border}`, display: "flex", flexDirection: "column", flexShrink: 0, position: "relative", transition: "width 0.15s,min-width 0.15s", overflow: "hidden" }}>
        <PanelToggle collapsed={rightCollapsed} onClick={() => setRC(!rightCollapsed)} dir="right" />
        {!rightCollapsed && <>
          <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
            {rightTabs.map(([k, l]) => (
              <button key={k} onClick={() => setRightTab(k)} style={{ flex: 1, padding: "6px 2px", background: "none", border: "none", borderBottom: rightTab === k ? `2px solid ${T.blue}` : "2px solid transparent", color: rightTab === k ? T.blueLt : T.textMute, cursor: "pointer", fontSize: 9, fontWeight: 600 }}>{l}</button>
            ))}
          </div>
          {rightTab === "style" && <StylePanel node={selNode} onUpdate={n => selPath && updateNode_(selPath, n)} previewData={previewData} />}
          {rightTab === "groupdata" && isGroupSelected && <DataGroupPanel node={selNode} onUpdate={n => selPath && updateNode_(selPath, n)} screen={screen} previewData={previewData} setPreviewData={setPreviewData} screens={screens} allInputVars={allInputVars} dataSources={dataSources} />}
          {rightTab === "content" && !isGroupSelected && <ContentPanel node={selNode} onUpdate={n => selPath && updateNode_(selPath, n)} screens={screens} screen={screen} allInputVars={allInputVars} dataSources={dataSources} sourcePreviewData={sourcePreviewData} catalog={catalog} />}
          {rightTab === "catalog" && <CatalogPanel catalog={catalog} onChange={setCatalog} />}
          {rightTab === "kiosk" && <KioskSettingsPanel settings={settings} onChange={setSettings} screens={screens} />}
          {rightTab === "datasources" && <DataSourcesPanel dataSources={dataSources} onChange={setDataSources} sourcePreviewData={sourcePreviewData} setSourcePreviewData={setSourcePreviewData} allInputVars={allInputVars} />}
          {selNode && selPath && (
            <div style={{ padding: "5px 8px", borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 9, color: isGroupSelected ? T.yellow : T.blue, textTransform: "capitalize" }}>
                  {isGroupSelected ? `⬡ ${selNode.name}` : selNode.type}
                  {selPath.length > 1 && <span style={{ color: T.textMute, fontSize: 8 }}> d:{selPath.length}</span>}
                </span>
                <div style={{ display: "flex", gap: 3 }}>
                  {clipboard && (
                    <button
                      title={`Paste "${clipboard.node.name || clipboard.node.type}" (Ctrl+V)`}
                      onClick={() => {
                        const pasted = deepReId(clipboard.node);
                        const isSameScreen = clipboard.sourceScreenId === screen.id;
                        pasted.styles = { ...pasted.styles, x: (pasted.styles.x || 0) + (isSameScreen ? 20 : 0), y: (pasted.styles.y || 0) + (isSameScreen ? 20 : 0) };
                        if (pasted.type === "group") pasted.name = (pasted.name || "Group") + " Copy";
                        const targetPath = isGroupSelected ? selPath : null;
                        updCur(s => ({ ...s, children: insertNode(s.children, targetPath, pasted) }));
                        setSelPath(targetPath ? [...targetPath, pasted.id] : [pasted.id]);
                        showPasteToast(`✓ Pasted "${pasted.name || pasted.type}"`);
                      }}
                      style={{ background: T.bg, border: `1px solid ${T.blue}44`, borderRadius: 3, color: T.blueLt, cursor: "pointer", fontSize: 8, padding: "2px 5px" }}
                    >
                      📋 Paste
                    </button>
                  )}
                  {selPath.length > 1 && <button onClick={() => moveNodeOut(selPath)} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 3, color: T.textSub, cursor: "pointer", fontSize: 8, padding: "2px 5px" }}>↑ Out</button>}
                  {!isGroupSelected && <button onClick={() => {
                    const g = mkGroup();
                    g.styles = { ...g.styles, x: selNode.styles.x - 5, y: selNode.styles.y - 5, width: selNode.styles.width + 10, height: selNode.styles.height + 10 };
                    const wrapped = { ...selNode, styles: { ...selNode.styles, x: 5, y: 5 } }; g.children = [wrapped];
                    const pp = selPath.slice(0, -1);
                    if (!pp.length) updCur(s => ({ ...s, children: [...s.children.filter(c => c.id !== selNode.id), g] }));
                    else updCur(s => ({ ...s, children: updateNode(s.children, pp, p => ({ ...p, children: [...p.children.filter(c => c.id !== selNode.id), g] })) }));
                    setSelPath(pp.length ? [...pp, g.id] : [g.id]);
                  }} style={{ background: T.bg, border: `1px solid ${T.yellow}44`, borderRadius: 3, color: T.yellow, cursor: "pointer", fontSize: 8, padding: "2px 5px" }}>⬡ Wrap</button>}
                  <button onClick={() => deleteNode_(selPath)} style={{ background: T.bg, border: `1px solid ${T.red}44`, borderRadius: 3, color: T.red, cursor: "pointer", fontSize: 8, padding: "2px 5px" }}>✕</button>
                </div>
              </div>
            </div>
          )}
        </>}
      </div>

      {/* ── Copy/Paste Toast ── */}
      {pasteToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: pasteToast.isError ? "#7f1d1d" : "#1e3444",
          border: `1px solid ${pasteToast.isError ? "#ef4444" : "#6366f1"}`,
          color: pasteToast.isError ? "#fca5a5" : "#a5b4fc",
          padding: "8px 18px", borderRadius: 6, fontSize: 11, fontWeight: 600,
          zIndex: 99999, pointerEvents: "none", boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 8,
        }}>
          {pasteToast.msg}
        </div>
      )}
    </div>
  );
}

